import React, { useEffect, useRef, useCallback, useState } from 'react';
import { getObstacleIcon, getArpIcon, getShieldComboIcon } from '../../utils/markers';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import MapMeasureTool from '../../components/MapMeasureTool';
import Cesium3DPreviewModal from '../../components/Cesium3DPreviewModal';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield, exportSurfacePenetratingCsv } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';
import { assessVss } from '../../api/client';

// Flags the worst (highest) penetration per group as the controlling obstacle.
// Only penetrating rows are compared — VSS only carries a penetration magnitude
// when an obstacle actually penetrates (cleared obstacles have penetration_m = null).
function markControllingByPenetration(rows, groupKeyFn) {
    const worst = new Map();
    rows.forEach(r => {
        if (!r.penetrates || r.penetration_m == null) return;
        const key = groupKeyFn(r);
        const cur = worst.get(key);
        if (!cur || r.penetration_m > cur.penetration_m) worst.set(key, r);
    });
    worst.forEach(r => { r.isControlling = true; });
}

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OpenStreetMap contributors' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

const PENETRATE_COLOR = '#e84040';
const CLEAR_COLOR     = '#2EC98B';
const IGNORED_COLOR   = '#888888';

function getRectColor(icao, assessedList) {
    const PALETTE = ['#3a8ac4','#F4A300','#9b59b6','#2EC98B','#e67e22','#16a085','#8e44ad','#c0392b'];
    const idx = assessedList.indexOf(icao);
    return PALETTE[idx % PALETTE.length] || '#3a8ac4';
}

export default function VssPage({ project, arps, obstacles, loading, vssState, setVssState, showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady, onAerodromeContextMenu }) {
    const { getStyle } = useSurfaceStyles();
    const vssStyle = getStyle('vss.surface');
    const mapRef   = useRef(null);
    const mapInst  = useRef(null);
    const tileRef  = useRef(null);
    const layersRef= useRef([]);

    // Step 2 reference set — this project's own obstacles that actually
    // penetrate the VSS, not just its tallest point overall.
    const vssResultsForShield = vssState?.vssResults || {};
    const penetratingObstacleIds = React.useMemo(() => (
        Object.values(vssResultsForShield).flatMap(data => (data?.results || []).filter(r => r.penetrates).map(r => r.obstacle_id))
    ), [vssResultsForShield]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv, shieldLayerRef } = useShield(mapInst, obstacles, penetratingObstacleIds);

    const [vssAerodromes, setVssAerodromes]   = React.useState([]);
    const [mapMode, setMapMode]               = React.useState('street');
    const [assessing, setAssessing]           = React.useState({});
    const [activeIcao, setActiveIcao]         = React.useState(null);
    const [filterDir, setFilterDir]           = React.useState('ALL');
    const [capturedVss, setCapturedVss]       = React.useState({});
    const [resultsCollapsed, setResultsCollapsed] = React.useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(200, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = React.useState(false);

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);

    // Hoisted state
    const vssResults = vssState?.vssResults || {};
    const visVss     = vssState?.visVss     || {};
    const setVssResults = (val) => setVssState(prev => ({ ...prev, vssResults: typeof val === 'function' ? val(prev.vssResults || {}) : val }));
    const setVisVss     = (val) => setVssState(prev => ({ ...prev, visVss:     typeof val === 'function' ? val(prev.visVss     || {}) : val }));

    const obsWithCoords = React.useMemo(() => (obstacles || []).filter(o => o.latitude && o.longitude), [obstacles]);
    const assessedIcaos = React.useMemo(() => Object.keys(vssResults), [vssResults]);

    // ── VSS shielding — real, not flat ───────────────────────────────────────
    // Same reasoning as OLS/OLS ADG: the Visual Segment Surface is sloped
    // (rises from the threshold at the published VPA), so "shields" has to
    // mean "already penetrates THIS surface, at its own position, by at
    // least as much as the project's own worst obstacle does" — not "is
    // taller". Reuses assessVss, the exact engine already used for real
    // project obstacles. NOTE — VSS's sign convention is the opposite of
    // OLS's margin: penetration_m > 0 means penetrates (bigger = worse),
    // where OLS's margin < 0 means penetrates (more negative = worse).
    const [vssShieldPens, setVssShieldPens] = React.useState({}); // icao -> candidate result rows
    const [vssShieldLoading, setVssShieldLoading] = React.useState(false);

    const vssImpacts = React.useMemo(() => {
        const out = {}; // `${icao}_${designator}` -> worst (highest) penetration_m
        Object.entries(vssResults).forEach(([icao, data]) => {
            (data?.results || []).forEach(r => {
                if (!r.penetrates || r.penetration_m == null) return;
                const key = `${icao}_${r.designator}`;
                if (!(key in out) || r.penetration_m > out[key]) out[key] = r.penetration_m;
            });
        });
        return out;
    }, [vssResults]);
    const hasVssImpact = Object.keys(vssImpacts).length > 0;

    useEffect(() => {
        if (!shieldOn || !shieldData || !hasVssImpact) { setVssShieldPens({}); return; }
        const icaos = [...new Set(Object.keys(vssImpacts).map(k => k.split('_')[0]))];
        if (!icaos.length) return;

        const candidates = [
            ...(shieldData.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
            ...(shieldData.sof || []).map(r => ({ ...r, kind: 'SOF' })),
        ].filter(r => r.lat != null && r.lon != null);
        if (!candidates.length) { setVssShieldPens({}); return; }

        const pseudoObstacles = candidates.map(r => ({
            id: `shield_${r.kind}_${r.ogc_fid}`,
            name: r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type),
            latitude: r.lat, longitude: r.lon,
            elevation_m: parseFloat(r.elev_m) || 0,
            v_tolerance: parseFloat(r.v_tolerance_m) || 0,
            h_tolerance: 0,
        }));

        let cancelled = false;
        (async () => {
            setVssShieldLoading(true);
            const perIcao = {};
            for (const icao of icaos) {
                try {
                    const { data } = await assessVss(icao, pseudoObstacles);
                    // Keep every result (not just penetrating ones) so the
                    // popup can tell "outside the VSS / screened out" apart
                    // from "inside it but not penetrating enough to shield".
                    perIcao[icao] = data.results || [];
                } catch (e) {
                    console.error(`VSS shield check failed for ${icao}`, e);
                }
            }
            if (!cancelled) setVssShieldPens(perIcao);
            setVssShieldLoading(false);
        })();
        return () => { cancelled = true; };
    }, [shieldOn, shieldData, hasVssImpact, vssImpacts]);

    // Candidate pseudo-id -> { icao, designator, penetration_m, projectPenetration }
    const vssShieldVerdicts = React.useMemo(() => {
        const out = new Map();
        Object.entries(vssShieldPens).forEach(([icao, rows]) => {
            rows.forEach(r => {
                if (!r.penetrates || r.penetration_m == null) return;
                const key = `${icao}_${r.designator}`;
                const projectPenetration = vssImpacts[key];
                if (projectPenetration == null) return;
                // "Shields" = already penetrates as much or MORE (opposite
                // direction from OLS's margin comparison — see note above).
                if (r.penetration_m >= projectPenetration) {
                    out.set(r.obstacle_id, { icao, designator: r.designator, penetration_m: r.penetration_m, projectPenetration });
                }
            });
        });
        return out;
    }, [vssShieldPens, vssImpacts]);

    // Candidate pseudo-id -> true if VSS actually assessed it at all (i.e.
    // it wasn't screened out as "ignored" — below THR+15m — or otherwise
    // outside the surface) — kept separate from "shields" so the popup can
    // distinguish "outside surface" from "inside it but not penetrating enough".
    const vssShieldInsideSurface = React.useMemo(() => {
        const out = new Map();
        Object.values(vssShieldPens).forEach(rows => {
            rows.forEach(r => { if (!r.ignored) out.set(r.obstacle_id, true); });
        });
        return out;
    }, [vssShieldPens]);

    // Candidate pseudo-id -> [{ designator, penetration_m }, ...] for every
    // VSS runway it genuinely penetrates at its own position (r.penetrates),
    // regardless of raw elevation or of beating the project's worst
    // penetration — see olsShieldRealPenetration in OLSPage.jsx.
    const vssShieldRealPenetration = React.useMemo(() => {
        const out = new Map();
        Object.values(vssShieldPens).forEach(rows => {
            rows.forEach(r => {
                if (!r.penetrates) return;
                const list = out.get(r.obstacle_id) || [];
                list.push({ designator: r.designator, penetration_m: r.penetration_m });
                out.set(r.obstacle_id, list);
            });
        });
        return out;
    }, [vssShieldPens]);

    // Enrich each shield marker's own popup (from useShield) with the real
    // VSS verdict, same pattern as OLSPage.jsx. Also recolours the marker
    // yellow for a genuine real-surface penetration that wasn't already red
    // (raw-elevation shield).
    const comboShieldLayerRef = React.useRef(null); // half-red/half-yellow halo: red (raw-elevation) AND genuinely penetrates a real surface
    useEffect(() => {
        const group = shieldLayerRef.current;
        if (comboShieldLayerRef.current) { mapInst.current?.removeLayer(comboShieldLayerRef.current); comboShieldLayerRef.current = null; }
        if (!group || !hasVssImpact) return;
        const comboGroup = L.featureGroup();
        group.eachLayer(layer => {
            if (!layer._shieldKey || !layer._basePopupHtml) return;
            const v = vssShieldVerdicts.get(layer._shieldKey);
            const insideAnySurface = vssShieldInsideSurface.get(layer._shieldKey) || false;
            const realPenList = vssShieldRealPenetration.get(layer._shieldKey) || [];
            const realPenetration = realPenList.length > 0;
            const realPenNames = [...new Set(realPenList.map(v2 => `VSS RWY ${v2.designator}`))];
            const outsideLine = `<br/><span style="color:#8a94a6">Outside surface?: ${insideAnySurface ? 'No' : 'Yes'}</span>`;
            const penetratesLine = realPenetration
                ? `<br/><span style="color:#2D9CDB">Penetrates real surface (regardless of height)?: Yes — ${realPenNames.join(', ')}</span>`
                : `<br/><span style="color:#8a94a6">Penetrates real surface (regardless of height)?: No</span>`;
            const surfaceLine = v
                ? `<br/><b style="color:#FFB020">VSS: RWY ${v.designator}</b> ` +
                  `(this obstacle penetrates by ${v.penetration_m.toFixed(1)} m vs project's worst ${v.projectPenetration.toFixed(1)} m)`
                : `<br/><span style="color:#8a94a6">VSS: No</span>`;
            layer.setPopupContent(layer._basePopupHtml + outsideLine + penetratesLine + surfaceLine);
            if (realPenetration && !layer._shieldsRaw) {
                layer.setStyle({ color: '#FFE14D', fillColor: '#FFE14D', weight: 2, radius: 7, fillOpacity: 0.85 });
            } else if (realPenetration && layer._shieldsRaw) {
                const halo = L.marker(layer.getLatLng(), { icon: getShieldComboIcon(L), interactive: false });
                comboGroup.addLayer(halo);
            }
        });
        if (comboGroup.getLayers().length > 0) {
            comboShieldLayerRef.current = comboGroup;
            comboGroup.addTo(mapInst.current);
        }
    }, [shieldLayerRef, vssShieldVerdicts, vssShieldInsideSurface, vssShieldRealPenetration, hasVssImpact, shieldData, shieldOn]);

    // Distinct marker layer for every candidate confirmed to truly shield the VSS.
    const vssShieldLayerRef = useRef(null);
    useEffect(() => {
        if (!mapInst.current) return;
        if (vssShieldLayerRef.current) { mapInst.current.removeLayer(vssShieldLayerRef.current); vssShieldLayerRef.current = null; }
        if (!shieldOn || vssShieldVerdicts.size === 0) return;

        const group = L.featureGroup();
        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });

        vssShieldVerdicts.forEach((v, obsId) => {
            const r = byId.get(obsId);
            if (!r || r.lat == null || r.lon == null) return;
            const marker = L.circleMarker([r.lat, r.lon], {
                radius: 10, color: '#FFB020', weight: 3, fillColor: '#FFB020', fillOpacity: 0.35,
            });
            const label = r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
            marker.bindPopup(
                `<b>👁️ TRUE VSS SHIELD</b><br/>${r.kind}: ${label}<br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>` +
                `Aerodrome: ${v.icao} · RWY ${v.designator}<br/>` +
                `This obstacle's own VSS penetration: <b>${v.penetration_m.toFixed(1)} m</b><br/>` +
                `Project's worst-case penetration there: <b>${v.projectPenetration.toFixed(1)} m</b><br/>` +
                `<span style="color:#FFB020">Already penetrates as much or more — the project is not a new impact on the VSS.</span>`
            );
            group.addLayer(marker);
        });

        if (group.getLayers().length > 0) {
            vssShieldLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, vssShieldVerdicts, shieldData]);

    const validArps = React.useMemo(
        () => (arps || []).filter(a => 
            vssAerodromes.includes(a.icao?.toUpperCase()) && 
            (a.distance_km == null || a.distance_km <= 14.82)
        ),
        [arps, vssAerodromes]
    );

    // Load VSS-capable aerodromes
    useEffect(() => {
        (async () => {
            try {
                const { getVssAerodromes } = await import('../../api/client');
                const res = await getVssAerodromes();
                setVssAerodromes(res.data || []);
            } catch (e) { console.error('VSS aerodromes error:', e); }
        })();
    }, []);

    // Map init
    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6, zoomDelta: 0.7, zoomSnap: 0.7 });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        const ro = new ResizeObserver(() => mapInst.current?.invalidateSize());
        ro.observe(mapRef.current);
        setMapStateLocal(mapInst.current);
        onMapReady?.(mapInst.current);
        return () => { ro.disconnect(); mapInst.current?.remove(); mapInst.current = null; tileRef.current = null; setMapStateLocal(null); onMapReady?.(null); };
    }, []);

    const [mapState, setMapStateLocal] = React.useState(null);

    // Tile swap
    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const layer = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // Draw layers
    useEffect(() => {
        if (!mapInst.current || loading) return;
        layersRef.current.forEach(({ layer }) => mapInst.current.removeLayer(layer));
        layersRef.current = [];

        // ARP markers — hide if assessed and toggled off
        validArps.forEach(arp => {
            if (!arp.lat || !arp.lon) return;
            const icao = arp.icao?.toUpperCase();
            const isAssessed = !!vssResults[icao];
            const allVisible = !isAssessed || visVss[`${icao}_ALL`] !== false;
            if (!allVisible) return;
            const ll = [arp.lat, arp.lon];
            const m = L.marker(ll, { icon: getArpIcon(L, arp, showArpLabels) })
                .bindPopup(`<b>✈ ${arp.icao}</b><br/>${arp.name}`);
            if (onAerodromeContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onAerodromeContextMenu(arp, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current);
            layersRef.current.push({ key: `arp_${icao}`, layer: m });
        });

        // VSS trapezoids per threshold
        assessedIcaos.forEach(icao => {
            const data = vssResults[icao];
            if (!data) return;
            const rectColor = getRectColor(icao, assessedIcaos);
            const allVisible = visVss[`${icao}_ALL`] !== false;

            // Threshold markers
            (data.thresholds || []).forEach(thr => {
                if (!thr.lat || !thr.lon || !allVisible) return;
                const desig = thr.designator || '';
                const visKey = `${icao}_${desig}`;
                if (visVss[visKey] === false) return;
                const ll = [parseFloat(thr.lat), parseFloat(thr.lon)];
                const m = L.circleMarker(ll, { radius: 5, color: rectColor, fillColor: rectColor, fillOpacity: 0.9, weight: 2 })
                    .bindPopup(`<b>THR ${desig}</b><br/>Elev: ${thr.elev_m ?? '—'} m`);
                m.addTo(mapInst.current);
                layersRef.current.push({ key: `${icao}_thr_${desig}`, layer: m });
            });

            // VSS trapezoid polygons per threshold
            (data.features?.features || []).forEach(f => {
                const desig = f.properties?.designator || 'unknown';
                const visKey = `${icao}_${desig}`;
                if (!allVisible || visVss[visKey] === false) return;
                const coords = f.geometry?.coordinates?.[0];
                if (!coords) return;
                const latlngs = coords.map(([lon, lat]) => [lat, lon]);
                const poly = L.polygon(latlngs, {
                    color: rectColor, weight: 2,
                    fillColor: vssStyle.color, fillOpacity: vssStyle.opacity,
                    dashArray: '5,3'
                });
                const p = f.properties;
                poly.bindTooltip(
                    `<b>${icao} – VSS RWY ${desig}</b><br/>` +
                    `VSS slope: ${p.vss_slope_deg}° &nbsp; OCA: ${p.oca_ft} ft<br/>` +
                    `Length from THR: ${(p.length_from_thr_m / 1852).toFixed(2)} NM &nbsp; Width at end: ${p.width_at_end_m} m`,
                    { sticky: true }
                );
                poly.addTo(mapInst.current);
                layersRef.current.push({ key: `${icao}_vss_${desig}`, layer: poly });
            });
        });

        // Obstacle markers
        getObstaclesToRender(obsWithCoords, project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude];
            let isPenetrating = false;
            let isInArea = false;
            let isIgnored = false;
            let isAssessed = false;
            let maxPen = -Infinity;
            let foundAny = false;
            let surfaceStr = '—';

            assessedIcaos.forEach(icao => {
                if (vssResults[icao]) isAssessed = true;
                const results = vssResults[icao]?.results || [];
                const rows = results.filter(r => r.obstacle_id === o.id);
                if (rows.length) isInArea = true;
                if (rows.some(r => r.penetrates)) isPenetrating = true;
                if (rows.some(r => r.ignored)) isIgnored = true;

                rows.forEach(r => {
                    foundAny = true;
                    const pen = r.penetration_m ?? 0;
                    if (pen > maxPen) {
                        maxPen = pen;
                        surfaceStr = `VSS RWY ${r.designator}`;
                    }
                });
            });

            let penetrationStr = '—';
            if (foundAny) {
                if (maxPen > 0) {
                    penetrationStr = `${maxPen.toFixed(2)} m`;
                } else {
                    penetrationStr = `${maxPen.toFixed(2)} m (Clear)`;
                }
            }

            const color = isPenetrating ? PENETRATE_COLOR : isIgnored ? IGNORED_COLOR : isInArea ? CLEAR_COLOR : '#555';
            const icon = getObstacleIcon(L, o, isAssessed ? (isPenetrating ? 'penetrated' : 'clear') : undefined, showObsLabels);
            const m = L.marker(ll, { icon })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>PENETRATION: ${penetrationStr}`);
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current);
            layersRef.current.push({ key: `obs_${o.id}`, layer: m });
        });
        addHullPolygonsToMap(mapInst.current, obsWithCoords, project?.id, layersRef, (l, i) => ({ key: `hull_poly_${i}`, layer: l }));
    }, [validArps, obstacles, loading, vssResults, visVss, showObstacles, showObsLabels, showArpLabels, assessedIcaos, onObstacleContextMenu, onAerodromeContextMenu, vssStyle]);

    // Run VSS assessment
    const runAssessment = useCallback(async (icao) => {
        setAssessing(prev => ({ ...prev, [icao]: true }));
        try {
            const { assessVss } = await import('../../api/client');
            const res = await assessVss(icao, obsWithCoords.map(o => ({
                id: o.id, name: o.name,
                latitude: o.latitude, longitude: o.longitude,
                elevation_m: o.elevation_m, v_tolerance: o.v_tolerance, h_tolerance: o.h_tolerance
            })));
            setVssResults(prev => ({ ...prev, [icao]: res.data }));
            const designators = (res.data.thresholds || []).map(t => t.designator).filter(Boolean);
            setVisVss(prev => {
                const next = { ...prev };
                if (next[`${icao}_ALL`] === undefined) next[`${icao}_ALL`] = true;
                designators.forEach(d => { if (next[`${icao}_${d}`] === undefined) next[`${icao}_${d}`] = true; });
                return next;
            });
            // Fit map to this aerodrome once
            const arp = validArps.find(a => a.icao?.toUpperCase() === icao);
            if (arp?.lat && arp?.lon && mapInst.current) {
                const thrs = res.data.thresholds || [];
                const pts = [[arp.lat, arp.lon], ...thrs.map(t => [parseFloat(t.lat), parseFloat(t.lon)]).filter(([a, b]) => !isNaN(a) && !isNaN(b))];
                mapInst.current.fitBounds(L.latLngBounds(pts), { padding: [80, 80], maxZoom: 13 });
            }
            setActiveIcao(icao);
            setFilterDir('ALL');
        } catch (err) { console.error('VSS assessment error:', err); }
        finally { setAssessing(prev => ({ ...prev, [icao]: false })); }
    }, [obsWithCoords, validArps]);

    // Screenshot capture
    const captureVssScreenshot = useCallback(async (icao) => {
        const map = mapInst.current;
        if (!map) return;
        try {
            const container = map.getContainer();
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch { }
            }));

            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = '';
                clone.style.left = '0';
                clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { 
                        ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); 
                        URL.revokeObjectURL(url); resolve(); 
                    };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }
            
            const divIconEls = [...container.querySelectorAll('.leaflet-marker-icon div')];
            for (const el of divIconEls) {
                if (el.querySelector('div')) continue;
                const eRect = el.getBoundingClientRect();
                const x = Math.round(eRect.left - containerRect.left);
                const y = Math.round(eRect.top - containerRect.top);
                const text = el.textContent?.trim();
                if (text) {
                    ctx.save();
                    ctx.font = 'bold 11px sans-serif';
                    ctx.fillStyle = el.style.color || '#ff9800';
                    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
                    ctx.lineWidth = 3;
                    ctx.strokeText(text, x, y + 10);
                    ctx.fillText(text, x, y + 10);
                    ctx.restore();
                }
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            const { default: api } = await import('../../api/client');
            await api.post('/vss/screenshot', { icao: icao.toUpperCase(), image: dataUrl, project_id: project?.id });
            setCapturedVss(prev => ({ ...prev, [icao]: true }));
        } catch (err) { console.error('VSS screenshot error:', err); }
    }, []);

    const clearVssScreenshot = useCallback(async (icao) => {
        try {
            const { deleteVssScreenshot } = await import('../../api/client');
            await deleteVssScreenshot(icao, project?.id);
            setCapturedVss(prev => {
                const next = { ...prev };
                delete next[icao];
                return next;
            });
        } catch (err) { console.error('Clear VSS screenshot error:', err); }
    }, []);

    const [preview3DIcao, setPreview3DIcao] = useState(null);

    const handleDownloadKmz = useCallback(async (icao, mode) => {
        try {
            const API_BASE = import.meta.env.VITE_API_URL || '/api';
            const url = `${API_BASE}/vss/${icao}/kml?mode=${mode}${project?.id ? `&project_id=${project.id}` : ''}`;
            
            const response = await fetch(url);
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Failed to download KMZ: ${text}`);
            }
            
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${icao}_VSS_${mode.toUpperCase()}.kmz`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('KMZ Export error:', err);
            alert("Failed to export KMZ: " + err.message);
        }
    }, []);

    const resetVssAssessment = useCallback((icao) => {
        if (!window.confirm(`Reset VSS assessment for ${icao}? This will remove it from the report.`)) return;
        setVssResults(prev => {
            const next = { ...prev };
            delete next[icao];
            return next;
        });
        if (activeIcao === icao) setActiveIcao(null);
    }, [activeIcao, setVssResults]);

    // Derived state for results panel
    const activeData = activeIcao ? vssResults[activeIcao] : null;
    if (activeData?.results?.length) {
        markControllingByPenetration(activeData.results, r => r.designator);
    }
    const filteredResults = React.useMemo(() => {
        if (!activeData?.results) return [];
        return activeData.results.filter(r => {
            if (r.ignored) return false;  // hide screened-out obstacles
            if (filterDir === 'ALL') return true;
            return r.designator === filterDir;
        });
    }, [activeData, filterDir]);

    const designators = React.useMemo(() => {
        if (!activeData?.thresholds) return [];
        return activeData.thresholds.map(t => t.designator).filter(Boolean);
    }, [activeData]);

    const penetrationCount = filteredResults.filter(r => r.penetrates).length;

    if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.muted }}>Loading…</div>;

    const renderResultsContent = () => {
        if (!activeIcao) {
            return (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: T.muted, fontSize: 12 }}>
                    Click <strong style={{ color: T.blue }}>⬇ Load</strong> on an aerodrome, then 📋 to view its results.
                </div>
            );
        }

        if (!activeData) return null;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                {/* Toolbar */}
                <div style={{ padding: '6px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
                    {penetrationCount > 0 && (
                        <div style={{ fontSize: 11, color: T.danger, fontWeight: 700, background: T.danger + '22', border: `1px solid ${T.danger}44`, borderRadius: 5, padding: '2px 8px' }}>
                            ⚠ {penetrationCount} VSS penetration{penetrationCount > 1 ? 's' : ''} — Detailed Analysis Required
                        </div>
                    )}
                    {penetrationCount === 0 && filteredResults.length > 0 && (
                        <div style={{ fontSize: 11, color: T.safe, fontWeight: 700 }}>✓ All obstacles clear of VSS</div>
                    )}
                    <div style={{ flex: 1 }} />
                    {filteredResults.length > 0 && (
                        <button
                            onClick={() => {
                                const headers = ["Obstacle Name / ID", "RWY (Threshold)", "Obstacle Elevation (m)", "VSS Elevation (m)", "Penetration (m)", "Minima Source", "Comment", "Controlling"];
                                const rows = filteredResults.map(r => [
                                    r.obstacle_name,
                                    `RWY ${r.designator}`,
                                    r.obstacle_elev_m,
                                    r.vss_elev_m ?? '-',
                                    r.penetrates ? `+${r.penetration_m}` : '-',
                                    r.generic_och ? 'Generic 500 ft OCH' : 'Published',
                                    r.comment,
                                    r.isControlling ? 'Yes' : ''
                                ]);
                                exportToCsv(headers, rows, `${activeIcao}_VSS_Assessment.csv`);
                            }}
                            style={{
                                fontSize: 10, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                background: T.blue, color: T.white, display: 'flex', alignItems: 'center', gap: 4, marginRight: 8
                            }}
                        >
                            📥 Export CSV
                        </button>
                    )}
                    {/* Direction filter */}
                    <div style={{ display: 'flex', gap: 4 }}>
                        {['ALL', ...designators].map(d => (
                            <button key={d} onClick={() => setFilterDir(d)} style={{
                                padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 10,
                                background: filterDir === d ? T.blue : 'rgba(255,255,255,0.07)',
                                color: filterDir === d ? T.white : T.muted, fontWeight: filterDir === d ? 700 : 400,
                            }}>{d === 'ALL' ? 'All' : `RWY ${d}`}</button>
                        ))}
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {filteredResults.length === 0 ? (
                        <div style={{ padding: '20px 12px', textAlign: 'center', color: T.muted, fontSize: 12 }}>
                            {activeData.results?.length === 0 ? 'No obstacles found within the VSS trapezoids.' : 'No obstacles for selected threshold.'}
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>
                                {/* Report-format header rows */}
                                <tr>
                                    <td colSpan={7} style={{ padding: '7px 12px', textAlign: 'center', fontWeight: 800, fontSize: 12, color: T.white, background: T.bg0, borderBottom: `1px solid ${T.border}`, letterSpacing: '0.5px' }}>
                                        {(() => { const arp = validArps.find(a => a.icao?.toUpperCase() === activeIcao); return `${arp?.name?.toUpperCase() || activeIcao} — ${activeIcao} Visual Segment Surface (VSS)`; })()}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={7} style={{ padding: '5px 12px', textAlign: 'center', fontSize: 11, color: capturedVss[activeIcao] ? T.safe : T.muted, background: capturedVss[activeIcao] ? T.safe + '11' : 'rgba(255,255,255,0.03)', borderBottom: `1px solid ${T.border}`, fontStyle: 'italic' }}>
                                        {capturedVss[activeIcao] ? 'Map screenshot captured for report' : 'PICTURE — use "Capture Screenshot" in aerodromes panel to capture map'}
                                    </td>
                                </tr>
                                <tr style={{ background: T.bg0, position: 'sticky', top: 0, zIndex: 1 }}>
                                    {['Obstacle Name / ID', 'RWY (Threshold)', 'Obstacle Elevation (m)', 'VSS Elevation (m)', 'Penetration (m)', 'Minima Source', 'Comment'].map(h => (
                                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredResults.map((r, i) => (
                                    <tr key={i} style={{ borderBottom: `1px solid ${T.border}22`, background: r.penetrates ? T.danger + '11' : 'transparent' }}>
                                        <td style={{ padding: '7px 10px', color: T.white, fontWeight: 600 }}>
                                            {r.obstacle_name} {r.isControlling && <span style={{ color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                        </td>
                                        <td style={{ padding: '7px 10px', color: T.blue, fontWeight: 700 }}>RWY {r.designator}</td>
                                        <td style={{ padding: '7px 10px', color: T.white }}>{r.obstacle_elev_m}</td>
                                        <td style={{ padding: '7px 10px', color: '#F4D03F' }}>{r.vss_elev_m ?? '—'}</td>
                                        <td style={{ padding: '7px 10px', color: r.penetrates ? T.danger : T.muted, fontWeight: r.penetrates ? 700 : 400 }}>
                                            {r.penetrates ? `+${r.penetration_m}` : '—'}
                                        </td>
                                        <td style={{ padding: '7px 10px' }}>
                                            {r.generic_och
                                                ? <span style={{ background: '#F4A30022', color: '#F4A300', border: '1px solid #F4A30066', borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>Generic 500 ft OCH</span>
                                                : <span style={{ color: T.safe, fontSize: 10 }}>✓ Published</span>}
                                        </td>
                                        <td style={{ padding: '7px 10px', color: r.penetrates ? T.danger : T.safe, fontSize: 10 }}>{r.comment}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Ignored obstacles footnote */}
                    {activeData.results?.some(r => r.ignored) && (
                        <div style={{ padding: '8px 16px', background: 'rgba(136,136,136,0.08)', borderTop: `1px solid ${T.border}33`, fontSize: 10, color: T.muted }}>
                            ℹ Some obstacles were screened out (below THR + 15 m) and are not shown in the results table.
                        </div>
                    )}

                    {penetrationCount > 0 && (
                        <div style={{ padding: '10px 16px', background: T.danger + '11', borderTop: `1px solid ${T.danger}33`, fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
                            <span style={{ color: T.danger, fontWeight: 700 }}>⚠ VSS Penetration Notice:</span>{' '}
                            One or more obstacles penetrate the Visual Segment Surface. A detailed approach-specific analysis is required.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* ── Left panel ── */}
            <div style={{ width: 240, flexShrink: 0, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}`, fontSize: 12, fontWeight: 800, color: T.white, letterSpacing: 0.5 }}>
                    🌫 VSS Aerodromes
                </div>


                {/* Aerodrome list */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                    {validArps.length === 0 && (
                        <div style={{ padding: '16px 14px', color: T.muted, fontSize: 11 }}>No VSS aerodromes found.</div>
                    )}
                    {validArps.map(arp => {
                        const icao = arp.icao?.toUpperCase();
                        const isAssessed = !!vssResults[icao];
                        const isAssessingNow = !!assessing[icao];
                        const allVis = visVss[`${icao}_ALL`] !== false;
                        const color = getRectColor(icao, assessedIcaos);
                        const thrs = isAssessed ? (vssResults[icao]?.thresholds || []) : [];

                        return (
                            <div key={icao} style={{ borderBottom: `1px solid ${T.border}22`, padding: '8px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                                    <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.white }}>{icao}</span>
                                    {/* Screenshot button */}
                                    {isAssessed && (
                                        <div style={{ display: 'flex', gap: 2 }}>
                                            <button title="Capture map screenshot for report" onClick={() => captureVssScreenshot(icao)} style={{
                                                background: capturedVss[icao] ? T.safe + '22' : 'rgba(255,255,255,0.07)',
                                                border: `1px solid ${capturedVss[icao] ? T.safe : T.border}`,
                                                color: capturedVss[icao] ? T.safe : T.muted,
                                                borderRadius: 4, padding: '2px 5px', cursor: 'pointer', fontSize: 12,
                                            }}>{capturedVss[icao] ? '✓ Captured' : 'Capture Screenshot'}</button>
                                            {capturedVss[icao] && (
                                                <button title="Clear screenshot" onClick={() => clearVssScreenshot(icao)} style={{
                                                    background: 'rgba(255,255,255,0.07)', border: `1px solid ${T.border}`,
                                                    color: T.danger, borderRadius: 4, padding: '2px 5px', cursor: 'pointer', fontSize: 10,
                                                }}>✕</button>
                                            )}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDownloadKmz(icao, '2d'); }}
                                                    title="Download 2D KMZ (Flat Footprints)"
                                                    style={{
                                                        background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`,
                                                        borderRadius: 4, padding: '1px 4px', cursor: 'pointer', marginLeft: 2,
                                                        color: T.muted, fontSize: 9
                                                    }}
                                                >
                                                    KMZ (2D)
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDownloadKmz(icao, '3d'); }}
                                                    title="Download 3D KMZ (True Altitudes)"
                                                    style={{
                                                        background: 'rgba(244,163,0,0.15)', border: `1px solid #F4A30044`,
                                                        borderRadius: 4, padding: '1px 4px', cursor: 'pointer', marginLeft: 2,
                                                        color: '#F4A300', fontSize: 9
                                                    }}
                                                >
                                                    KMZ (3D)
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setPreview3DIcao(icao); }}
                                                    title="Preview the VSS surfaces and project obstacles in 3D, right here"
                                                    style={{
                                                        background: 'rgba(46,204,113,0.15)', border: '1px solid #2ECC7144',
                                                        borderRadius: 4, padding: '1px 4px', cursor: 'pointer', marginLeft: 2,
                                                        color: '#2ECC71', fontSize: 9
                                                    }}
                                                >
                                                    🌐 3D
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {/* Assess / Results buttons */}
                                    <button onClick={() => runAssessment(icao)} disabled={isAssessingNow} style={{
                                        padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 10,
                                        background: isAssessed ? T.blue + '33' : T.blue,
                                        color: isAssessed ? T.blue : '#fff', fontWeight: 700,
                                    }}>{isAssessingNow ? '…' : isAssessed ? '↻' : '⬇ Load'}</button>
                                    {isAssessed && (
                                        <div style={{ display: 'flex', gap: 2 }}>
                                            <button onClick={() => setActiveIcao(icao)} style={{
                                                padding: '2px 6px', borderRadius: 4, border: `1px solid ${T.border}`,
                                                background: activeIcao === icao ? T.blue + '33' : 'transparent',
                                                color: activeIcao === icao ? T.blue : T.muted, cursor: 'pointer', fontSize: 12,
                                            }}>📋</button>
                                            <button title="Reset Assessment (remove from report)" onClick={() => resetVssAssessment(icao)} style={{
                                                padding: '2px 6px', borderRadius: 4, border: `1px solid ${T.border}`,
                                                background: 'transparent', color: T.danger, cursor: 'pointer', fontSize: 12,
                                            }}>🗑️</button>
                                        </div>
                                    )}
                                </div>

                                {/* Visibility toggles for assessed aerodromes */}
                                {isAssessed && (
                                    <div style={{ paddingLeft: 16, fontSize: 10, color: T.muted }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={allVis} onChange={e => {
                                                const val = e.target.checked;
                                                setVisVss(prev => {
                                                    const next = { ...prev, [`${icao}_ALL`]: val };
                                                    thrs.forEach(thr => { next[`${icao}_${thr.designator}`] = val; });
                                                    return next;
                                                });
                                             }} />
                                            <span style={{ fontWeight: 700, color: allVis ? T.white : T.muted }}>All surfaces</span>
                                        </label>
                                        {thrs.map(thr => {
                                            const d = thr.designator || '';
                                            const visKey = `${icao}_${d}`;
                                             const vis = visVss[visKey] !== false;
                                            return (
                                                <label key={d} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2, cursor: 'pointer', paddingLeft: 8 }}>
                                                    <input type="checkbox" checked={vis}
                                                         onChange={e => {
                                                             const val = e.target.checked;
                                                             setVisVss(prev => {
                                                                 const next = { ...prev, [visKey]: val };
                                                                 if (val) next[`${icao}_ALL`] = true;
                                                                 return next;
                                                             });
                                                         }} />
                                                    <span style={{ color: vis ? T.white : T.muted }}>RWY {d}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Legend */}
                <div style={{ padding: '10px 14px', borderTop: `1px solid ${T.border}`, fontSize: 10, color: T.muted }}>
                    <div style={{ fontWeight: 700, marginBottom: 5, color: T.white }}>Legend</div>
                    {assessedIcaos.map(icao => (
                        <div key={icao} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                            <div style={{ width: 10, height: 4, borderRadius: 2, background: getRectColor(icao, assessedIcaos), opacity: 0.8 }} />
                            <span>{icao}</span>
                        </div>
                    ))}
                    {assessedIcaos.length === 0 && <span style={{ fontStyle: 'italic' }}>No surfaces loaded</span>}
                    <div style={{ marginTop: 4, borderTop: `1px solid ${T.border}33`, paddingTop: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}><div style={{ width: 10, height: 10, borderRadius: 50, background: PENETRATE_COLOR }} />Penetrates VSS</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}><div style={{ width: 10, height: 10, borderRadius: 50, background: CLEAR_COLOR }} />Clear of VSS</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 10, height: 10, borderRadius: 50, background: IGNORED_COLOR }} />Ignored (below THR+15m)</div>
                    </div>
                </div>
            </div>

            {/* ── Map + results ── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)', alignItems: 'center' }}>
                    {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                        <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                    ))}
                </div>
                <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv}
                    penetratingCount={vssShieldRealPenetration.size}
                    onExportPenetrating={() => {
                        const byId = new Map();
                        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
                         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
                            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
                        });
                        const entries = [];
                        vssShieldRealPenetration.forEach((penList, obsId) => {
                            const r = byId.get(obsId);
                            if (!r) return;
                            penList.forEach(v => entries.push({
                                r,
                                surfaceLabel: `VSS RWY ${v.designator}`,
                                penetrationAmount: v.penetration_m.toFixed(2),
                            }));
                        });
                        exportSurfacePenetratingCsv(entries, radiusKm);
                    }} />
                {shieldOn && hasVssImpact && (
                    <div style={{ position: 'absolute', top: 226, right: 12, zIndex: 1000, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 8, border: '1px solid #FFB02055', backdropFilter: 'blur(8px)', maxWidth: 260, fontSize: 10, color: T.muted, lineHeight: 1.5 }}>
                        <div style={{ color: '#FFB020', fontWeight: 700, marginBottom: 2 }}>👁️ VSS (real check)</div>
                        {vssShieldLoading ? 'Checking real penetration…' : (
                            vssShieldVerdicts.size > 0
                                ? <span><b style={{ color: '#FFB020' }}>{vssShieldVerdicts.size}</b> obstacle(s) confirmed to already penetrate the VSS as much or more than this project's worst case — not a new impact.</span>
                                : 'No candidate already penetrates the VSS at least as much as this project — flat elevation comparison alone would be misleading here.'
                        )}
                    </div>
                )}
                <MapMeasureTool map={mapState} top={52} tableOffset={resultsCollapsed ? 40 : (activeIcao ? 200 : 0)} />
                <MapCoordinateViewer map={mapState} />
                <div ref={mapRef} style={{ flex: 1 }} />

                {/* Results table */}
                <div style={{ height: activeIcao ? (resultsCollapsed ? 38 : resultsHeight) : 0, background: T.bg1, borderTop: activeIcao ? `1px solid ${T.border}` : 'none', display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden', flexShrink: 0 }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                    <div style={{ padding: '8px 20px', background: T.bg0, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🏔</span> VSS Results
                        </div>
                        {activeIcao && (
                            <div style={{ display: 'flex', gap: 12 }}>
                                {!resultsPoppedOut && (
                                    <button
                                        onClick={() => {
                                            setResultsPoppedOut(true);
                                            setResultsCollapsed(true);
                                        }}
                                        style={{
                                            background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                            display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                        }}
                                        title="Pop out results to a movable window"
                                    >
                                        🗖 Pop Out
                                    </button>
                                )}
                                <button
                                    onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                    style={{
                                        background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                        fontWeight: 600
                                    }}
                                >
                                    {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                                </button>
                            </div>
                        )}
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {resultsPoppedOut ? (
                            <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                Results are popped out. 
                                <button 
                                    onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }} 
                                    style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                >
                                    Bring back
                                </button>
                            </div>
                        ) : renderResultsContent()}
                    </div>
                </div>
            </div>

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - VSS Results`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={850}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}

            {preview3DIcao && (
                <Cesium3DPreviewModal
                    title={`${preview3DIcao} — VSS 3D Preview`}
                    kmlUrl={`${import.meta.env.VITE_API_URL || '/api'}/vss/${preview3DIcao}/kml?mode=3d${project?.id ? `&project_id=${project.id}` : ''}`}
                    onClose={() => setPreview3DIcao(null)}
                />
            )}
        </div>
    );
}
